import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PlaygroundSession } from "./types";

// AI-6337: bumped from [2s, 4s, 6s] = 12s total to a ~90s budget. The
// summary worker fires Haiku from inside the user-view-handler branch
// of ``ask_session_stream`` and end-to-end intent generation can take
// 60-100s on slower flows (large query_group contexts, slow MCP tool
// calls). A 12s poll exited well before the intent landed, leaving the
// sidebar stuck on the raw ``purpose`` until the user hard-refreshed.
const POLL_INTERVALS_MS = [2000, 4000, 8000, 15000, 25000, 35000];

/**
 * Bounded polling for session-intent backfill in the sidebar.
 *
 * NOTE ON FILE NAME: this file is named ``usePlaygroundSidebarSummaryPoll``
 * for git-history continuity, but it has NOTHING to do with per-turn
 * ``ChatBlock.summary`` (an unrelated field on individual chat turns).
 * What this hook polls is the per-session ``session_intent`` field on
 * ``PlaygroundSession`` — the Haiku-generated one-line title shown as
 * the sidebar row text. If you're hunting a bug in per-turn summaries,
 * this is the wrong file.
 *
 * Mechanism:
 * - Backend lazily generates ``session_intent`` per session via Haiku
 *   (concurrency=5). The first sidebar load enqueues every row whose
 *   intent is still ``null``; subsequent generations land in the DB
 *   asynchronously.
 * - This hook re-fetches the ``["playground-sessions", ...]`` React
 *   Query at fixed intervals [2s, 4s, 6s] — 12s total budget — so the
 *   UI picks up new intents as they land server-side.
 * - The schedule is driven by a single recursive ``setTimeout`` chain,
 *   not effect-based re-scheduling. This guarantees the full schedule
 *   runs even when the backend returns structurally identical data
 *   between polls (React Query's structural sharing keeps the same
 *   ``sessions`` reference, so a deps-driven effect would silently
 *   stall after attempt 1).
 * - Polling stops when ``hasMissing`` flips false (every row has an
 *   intent) or the schedule is exhausted, whichever comes first. The
 *   effect cleanup cancels any pending tick on ``resetKey`` change or
 *   unmount.
 */
export const usePlaygroundSidebarSummaryPoll = (
  sessions: PlaygroundSession[],
  resetKey: string
) => {
  const queryClient = useQueryClient();
  const hasMissing = sessions.some(s => !s.session_intent);

  useEffect(() => {
    if (!hasMissing) return;

    let cancelled = false;
    let attempt = 0;
    let timerId: ReturnType<typeof setTimeout> | undefined;

    const tick = () => {
      if (cancelled || attempt >= POLL_INTERVALS_MS.length) return;
      timerId = setTimeout(() => {
        if (cancelled) return;
        attempt += 1;
        queryClient.invalidateQueries({ queryKey: ["playground-sessions"] });
        tick();
      }, POLL_INTERVALS_MS[attempt]);
    };

    tick();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [hasMissing, resetKey, queryClient]);
};
